import React from 'react';
import { usePropertyFormContext } from '../contexts/PropertyFormContext';
import { GeneralInfo } from './PropertyDetails/sections/GeneralInfo';
import { Rooms } from './PropertyDetails/sections/Rooms';
import { ApartmentOptions } from './PropertyDetails/sections/ApartmentOptions';
import { LandOptions } from './PropertyDetails/sections/LandOptions';
import { PromotionOptions } from './PropertyDetails/sections/PromotionOptions';
import { CommercialOptions } from './PropertyDetails/sections/CommercialOptions';
import { FormField } from '../components/FormField';
import { FormRow } from '../components/FormRow';
import { Button } from '../../../ui/Button';

interface PropertyDetailsProps {
  onNext: () => void;
}

export const PropertyDetails: React.FC<PropertyDetailsProps> = ({ onNext }) => {
  const { formData, property, handleInputChange } = usePropertyFormContext();
  const isApartment = formData.propertyType?.toLowerCase() === 'appartement';
  const isHouse = formData.propertyType?.toLowerCase() === 'maison';
  const isLand = formData.propertyType?.toLowerCase() === 'terrain';
  const isPromotion = formData.propertyType?.toLowerCase() === 'promotion';
  const isCommercial = formData.propertyType?.toLowerCase() === 'commerce';

  if (!formData.propertyType) {
    return (
      <p className="text-gray-500 dark:text-gray-400">
        Veuillez d'abord sélectionner un type de bien dans l'onglet "Informations Générales".
      </p>
    );
  }

  const renderContent = () => {
    if (isPromotion) {
      return (
        <>
          <PromotionOptions
            formData={formData}
            property={property}
            handleInputChange={handleInputChange}
          />
          <div className="mt-6">
            <FormRow>
              <FormField label="Nombre de parkings extérieurs">
                <input
                  type="number"
                  name="outdoorParkingCount"
                  value={property?.outdoor_parking ?? formData.outdoorParkingCount ?? ''}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full"
                />
              </FormField>
              <FormField label="Nombre de parkings intérieurs">
                <input
                  type="number"
                  name="indoorParkingCount"
                  value={property?.indoor_parking ?? formData.indoorParkingCount ?? ''}
                  onChange={handleInputChange}
                  min="0"
                  className="w-full"
                />
              </FormField>
            </FormRow>
          </div>
        </>
      );
    }

    if (isLand) {
      return (
        <LandOptions
          formData={formData}
          handleInputChange={handleInputChange}
        />
      );
    }

    if (isCommercial) {
      return (
        <CommercialOptions
          formData={formData}
          handleInputChange={handleInputChange}
        />
      );
    }

    return (
      <>
        <GeneralInfo
          formData={formData}
          handleInputChange={handleInputChange}
          isHouse={isHouse}
        />

        <Rooms
          formData={formData}
          handleInputChange={handleInputChange}
        />

        {!isApartment && !isHouse && (
          <ApartmentOptions
            formData={formData}
            handleInputChange={handleInputChange}
          />
        )}

        <FormField label="Clé du bien">
          <textarea
            name="keyLocation"
            value={formData.keyLocation || ''}
            onChange={handleInputChange}
            rows={2}
            placeholder="Précisez l'emplacement des clés (ex: à l'agence, chez un collègue...)"
            className="w-full"
          />
        </FormField>

        <FormField label="Observations">
          <textarea
            name="observations"
            value={formData.observations || ''}
            onChange={handleInputChange}
            rows={4}
            placeholder="Notes importantes concernant le bien (ex: fenêtres à changer, climatiseur en panne...)"
            className="w-full"
          />
        </FormField>
      </>
    );
  };

  return (
    <div className="space-y-8">
      {renderContent()}

      <div className="flex justify-end">
        <Button onClick={onNext}>
          Suivant
        </Button>
      </div>
    </div>
  );
};